import { Controller } from "@hotwired/stimulus"
import { useStore } from "stimulus-store"
import { allPartsStore } from "../../stores/parts/all_parts_store"
import { sendFailureNotification, sendSuccessNotification } from "shared/toast_notifications"
import $ from "jquery"
import { getCsrfToken } from "shared/form_helpers";

// Connects to data-controller="forms--parts-form"
export default class extends Controller {
    static stores = [
        allPartsStore
    ]

    static targets = [
        "modal",
        "form",
        "title",
        "nameInput",
        "costInput",
        "idInput",
        "submitButton"
    ]

    static values = {
        mode: String,  // 'create' or 'edit'
        partId: String
    }

    connect() {
        useStore(this)
        this.createModal()
    }

    disconnect() {
        // Clean up modal when controller disconnects
        this.destroyModal()
    }

    /**
     * Handle add button click from window event (following facilities pattern)
     */
    handleAddButtonClick(event) {
        this.modeValue = 'create'
        this.partIdValue = ''

        // Reset button state to ensure it's enabled
        this.resetButtonState()

        this.titleTarget.textContent = 'Add New Part'
        this.showModal()
    }

    /**
     * Handle edit button click from window event (following facilities pattern)
     */
    handleEditButtonClick(event) {
        const partId = event.detail.partId
        const part = this.findPartById(partId)

        if (!part) {
            sendFailureNotification('Part not found')
            return
        }

        this.modeValue = 'edit'
        this.partIdValue = partId

        // Reset button state to ensure it's enabled
        this.resetButtonState()

        this.populateForm(part)
        this.titleTarget.textContent = 'Edit Part'
        this.showModal()
    }

    /**
     * Handle form submission (following facilities pattern)
     */
    handleSubmit(event) {
        // Prevent the default event operation from running
        event.preventDefault()

        try {
            this.showLoadingState()

            // Grab the route and method to call
            const endpoint = this.determineApiEndpoint()
            const method = this.modeValue === 'create' ? 'POST' : 'PATCH'

            // Grab the form data
            const formData = new FormData(event.target, event.submitter)

            // Issue the request using jQuery AJAX (following facilities pattern)
            $.ajax({
                url: endpoint,
                method: method,
                dataType: 'json',
                format: 'json',
                data: formData,
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },

                // This prevents jQuery from processing the FormData.
                // We need to do this to mix in additional values using FormData.append(...)
                processData: false,

                // Settings this to false lets the browser set the content type. This
                // is needed to mix in additional values using FormData.append(...)
                contentType: false,
            }).promise().then((result) => {
                this.handleSuccessResponse(result)
                this.hideLoadingState()
            }).catch((error) => {
                console.error('Form submission error:', error)
                const errorMessage = error.responseJSON?.message || `Unable to ${ this.modeValue } part: ${ error.statusText || 'Unknown error' }`
                sendFailureNotification(errorMessage)
                this.hideLoadingState()
            })
        } catch (error) {
            console.error('Form submission error:', error)
            sendFailureNotification(`Unable to ${ this.modeValue } part: ${ error.message }`)
            this.hideLoadingState()
        }
    }

    /**
     * Determine API endpoint based on mode
     */
    determineApiEndpoint() {
        if (this.modeValue === 'create') {
            return '/api/device_types/part/create'
        } else {
            return '/api/device_types/part/update'
        }
    }

    /**
     * Handle successful API response (updated for facilities pattern)
     */
    handleSuccessResponse(responseData) {
        const isCreate = this.modeValue === 'create'

        if (isCreate) {
            // Add new part to store
            const newPart = this.transformResponseToPart(responseData)
            const updatedParts = [...this.allPartsStoreValue, newPart]
            this.setAllPartsStoreValue(updatedParts)

            sendSuccessNotification('Part created successfully')
        } else {
            // Update existing part in store
            const updatedPart = this.transformResponseToPart(responseData)
            const updatedParts = this.allPartsStoreValue.map(part =>
                part.id === updatedPart.id ? updatedPart : part
            )
            this.setAllPartsStoreValue(updatedParts)

            sendSuccessNotification('Part updated successfully')
        }

        // Clear form and hide modal after successful operation
        this.clearForm()
        this.hideModal()
    }

    /**
     * Transform API response to part object for store
     */
    transformResponseToPart(responseData) {
        // Handle different response formats
        const partData = responseData.part || responseData.data || responseData

        return {
            id: partData.id,
            name: partData.name,
            cost: partData.default_cost || partData.cost || 'N/A'
        }
    }

    /**
     * Find part by ID in store
     */
    findPartById(partId) {
        return this.allPartsStoreValue.find(part => part.id === partId)
    }

    /**
     * Populate form with part data
     */
    populateForm(part) {
        this.nameInputTarget.value = part.name || ''
        this.costInputTarget.value = part.cost || ''
        this.idInputTarget.value = part.id || ''
    }

    /**
     * Clear form inputs
     */
    clearForm() {
        this.nameInputTarget.value = ''
        this.costInputTarget.value = ''
        this.idInputTarget.value = ''
    }

    /**
     * Show loading state
     */
    showLoadingState() {
        if (this.hasSubmitButtonTarget) {
            this.submitButtonTarget.disabled = true
            this.submitButtonTarget.textContent = 'Processing...'
        }
    }

    /**
     * Hide loading state
     */
    hideLoadingState() {
        if (this.hasSubmitButtonTarget) {
            this.submitButtonTarget.disabled = false
            this.submitButtonTarget.textContent = this.modeValue === 'create' ? 'Submit' : 'Submit'
        }
    }

    /**
     * Reset button state (ensures button is enabled and has correct text)
     */
    resetButtonState() {
        if (this.hasSubmitButtonTarget) {
            this.submitButtonTarget.disabled = false
            this.submitButtonTarget.textContent = 'Submit'
        }
    }

    /**
     * Create and configure Bootstrap modal
     */
    createModal() {
        if (!this.hasModalTarget) {
            return
        }

        // Initialize Bootstrap modal
        this.modal = new bootstrap.Modal(this.modalTarget, {
            backdrop: 'static',
            keyboard: true
        })

        // Listen for modal events
        this.modalTarget.addEventListener('hidden.bs.modal', () => {
            this.clearForm()
        })
    }

    /**
     * Show modal
     */
    showModal() {
        if (this.modal) {
            this.modal.show()
        }
    }

    /**
     * Hide modal
     */
    hideModal() {
        if (this.modal) {
            this.modal.hide()
        }
    }

    /**
     * Destroy modal
     */
    destroyModal() {
        if (this.modal) {
            this.modal.dispose()
            this.modal = null
        }
    }

    /**
     * Handle refresh request from table
     */
    handleRefreshRequest() {
        // Dispatch event to parts supplier to reload data
        this.dispatch('refreshRequested', { bubbles: true })
    }
}